import type { ContentBlogUnifiedQuery$variables } from './__generated__/ContentBlogUnifiedQuery.graphql.ts';
import type { ContentUnifiedQuery$variables } from './__generated__/ContentUnifiedQuery.graphql.ts';
import type { ContentUnifiedWithSpaceKeyQuery$variables } from './__generated__/ContentUnifiedWithSpaceKeyQuery.graphql.ts';

const DEFAULT_BYLINE_CONTRIBUTORS_FETCH_LIMIT = 4;

type SharedVariableOptions = {
	spaceKey?: string | null;
	versionOverride: number | null;
	isSSR: boolean;
	preloadLabels?: boolean;
	includeAgentTrustReceiptData?: boolean;
	includeBylineContributors?: boolean;
	includeAutomationDiscoveries?: boolean;
	includeMetadata?: boolean;
	includeClassification?: boolean;
	includeContentHeader?: boolean;
	includeContentHeaderAccess?: boolean;
	includeReactions?: boolean;
	includeRestrictions?: boolean;
	includeRestrictionAncestors?: boolean;
	useContentModeFragment?: boolean;
};

type ContentVariableOptions = SharedVariableOptions & {
	contentId: string;
	includeAnalyticsByline?: boolean;
	includeContentHeaderPublicLink?: boolean;
};

type PageContentVariableOptions = ContentVariableOptions & {
	agentSessionsUserId?: string | null;
};

type SpaceKeyVariableOptions = Omit<SharedVariableOptions, 'spaceKey'> & {
	spaceKey: string;
	agentSessionsUserId?: string | null;
};

const createSharedVariables = ({
	spaceKey,
	versionOverride,
	isSSR,
	preloadLabels = false,
	includeAgentTrustReceiptData = false,
	includeAutomationDiscoveries = false,
	includeMetadata = false,
	includeClassification = false,
	includeContentHeader = false,
	includeContentHeaderAccess = false,
	includeReactions = false,
	includeRestrictions = false,
	includeRestrictionAncestors = false,
	useContentModeFragment = false,
}: SharedVariableOptions) => ({
	spaceKey,
	versionOverride,
	isSSR,
	useNewContentTopper: true,
	...(preloadLabels || includeMetadata ? { preloadLabels: true } : {}),
	...(includeAgentTrustReceiptData ? { includeAgentTrustReceiptData: true } : {}),
	...(includeAutomationDiscoveries ? { includeAutomationDiscoveries: true } : {}),
	...(includeClassification || includeMetadata ? { includeClassification: true } : {}),
	...(includeContentHeader ? { includeContentHeader: true } : {}),
	...(includeContentHeaderAccess ? { includeContentHeaderAccess: true } : {}),
	...(includeReactions ? { includeReactions: true } : {}),
	...(includeRestrictions ? { includeRestrictions: true } : {}),
	...(includeRestrictionAncestors ? { includeRestrictionAncestors: true } : {}),
	...(useContentModeFragment ? { useContentModeFragment: true } : {}),
});

const createAgentSessionVariables = (agentSessionsUserId?: string | null) =>
	agentSessionsUserId
		? {
				agentSessionsPropertyKey: `cc-agent-mention-sessions:${agentSessionsUserId}`,
				fetchAgentSessionsMarker: true,
			}
		: {};

const createBylineVariables = (includeBylineContributors = false) =>
	includeBylineContributors
		? {
				includeBylineContributors: true,
				bylineContributorsLimit: DEFAULT_BYLINE_CONTRIBUTORS_FETCH_LIMIT,
			}
		: {};

const createAnalyticsBylineVariables = (includeAnalyticsByline = false) =>
	includeAnalyticsByline ? { includeAnalyticsByline: true } : {};

export const createContentUnifiedQueryVariables = ({
	contentId,
	agentSessionsUserId,
	includeBylineContributors,
	includeAnalyticsByline,
	includeContentHeaderPublicLink,
	...sharedOptions
}: PageContentVariableOptions): ContentUnifiedQuery$variables => ({
	contentId,
	...createSharedVariables(sharedOptions),
	...createAgentSessionVariables(agentSessionsUserId),
	...createBylineVariables(includeBylineContributors),
	...createAnalyticsBylineVariables(includeAnalyticsByline),
	...(includeContentHeaderPublicLink ? { includeContentHeaderPublicLink: true } : {}),
});

export const createContentBlogUnifiedQueryVariables = ({
	contentId,
	includeBylineContributors,
	includeAnalyticsByline,
	includeContentHeaderPublicLink,
	...sharedOptions
}: ContentVariableOptions): ContentBlogUnifiedQuery$variables => ({
	contentId,
	...createSharedVariables(sharedOptions),
	...createBylineVariables(includeBylineContributors),
	...createAnalyticsBylineVariables(includeAnalyticsByline),
	...(includeContentHeaderPublicLink ? { includeContentHeaderPublicLink: true } : {}),
});

export const createContentUnifiedWithSpaceKeyQueryVariables = ({
	spaceKey,
	agentSessionsUserId,
	includeBylineContributors,
	...sharedOptions
}: SpaceKeyVariableOptions): ContentUnifiedWithSpaceKeyQuery$variables => ({
	...createSharedVariables({ ...sharedOptions, spaceKey }),
	spaceKey,
	...createAgentSessionVariables(agentSessionsUserId),
	...(includeBylineContributors ? { includeBylineContributors: true } : {}),
});
